import FlatCard from "components/UI-lib/FlatCard";
import { CopyButton } from "components/UI-lib";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { UsageResp, UsageTableRow } from "server";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import { BarList } from "components/Graphs/BarList";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { DeploymentUsageTable } from "./DeploymentsUsageSummary";
import UsernameDisplayField from "components/UsernameDisplayField";
import { useQuery } from "@tanstack/react-query";
import agent from "server";
import { DateRange, DateRangePicker } from "components/Graphs/DateRangePicker";
import { displayGpuHours } from "components/utils";
import { OrgContext } from "contexts/OrgContext";
import { Tabs, TabsList, TabsTrigger } from "components/Graphs/Tabs";
import Pagination from "components/UI-lib/Pagination";

export type DisplayMode = "byEnv" | "byUser";

interface UsageSummaryProps {
  activeOrgId: string;
}

const UsageSummary: React.FC<UsageSummaryProps> = ({ activeOrgId }) => {
  const [selectedTab, setSelectedTab] = useState<"compute" | "deployments">(
    "compute"
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      from: firstDay,
      to: now,
    };
  });
  const [displayMode, setDisplayMode] = useState<DisplayMode>("byEnv");
  const orgContext = useContext(OrgContext);

  const getUsage = async (orgId: string, dateRange: DateRange | undefined) => {
    const res = await agent.Organizations.getUsage(
      orgId,
      dateRange
        ? {
            from: dateRange.from || new Date(),
            to: dateRange.to || new Date(),
          }
        : undefined
    );
    if (!res.success || !res.data) {
      throw new Error("Failed to fetch usage data");
    }
    return res.data.Usage;
  };

  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ["usage", activeOrgId, dateRange?.from, dateRange?.to],
    queryFn: () => getUsage(activeOrgId, dateRange),
    enabled: !!activeOrgId && !!dateRange?.from && !!dateRange?.to,
  });

  const getTotalCost = (selectedTab: string) => {
    let totalCost = 0.0;
    usageData
      ?.filter((usage) => {
        if (selectedTab === "compute") {
          return usage.UsageType === "compute" || usage.UsageType === "storage";
        } else {
          return usage.UsageType === "deployment";
        }
      })
      .forEach((transaction) => {
        if (transaction.Cost) {
          totalCost += parseFloat(transaction.Cost);
        }
      });
    try {
      return `${calc(totalCost).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })}`;
    } catch (error) {
      return totalCost;
    }
  };

  const getTotalHours = (selectedTab: string) => {
    const envIds = new Set<string>();
    let totalHours = 0.0;
    usageData
      ?.filter((usage) => {
        envIds.add(usage.EnvID);
        if (selectedTab === "compute") {
          return usage.UsageType === "compute" || usage.UsageType === "storage";
        } else {
          return usage.UsageType === "deployment";
        }
      })
      .forEach((transaction) => {
        if (envIds.has(transaction.EnvID) && transaction.TotalTime) {
          // only add hours once for each unique EnvID
          totalHours += transaction.TotalTime / 3600;
          envIds.delete(transaction.EnvID);
        }
      });
    try {
      return displayGpuHours(totalHours);
    } catch (error) {
      return totalHours;
    }
  };

  return (
    <>
      <div className="mt-6 border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex flex-row justify-between" aria-label="Tabs">
          <div className="-mb-px flex space-x-8">
            {["compute", "deployments"].map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab as "compute" | "deployments")}
                className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                      ${
                        selectedTab === tab
                          ? "border-highlight text-cyan-600 dark:text-highlight"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600"
                      }
                    `}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <Tabs
            className="flex items-center"
            value={displayMode}
            onValueChange={(val) => setDisplayMode(val as DisplayMode)}
          >
            <TabsList variant="solid">
              <TabsTrigger value="byEnv">
                By {selectedTab === "deployments" ? "Deployment" : "Instance"}
              </TabsTrigger>
              <TabsTrigger value="byUser">By User</TabsTrigger>
            </TabsList>
          </Tabs>
        </nav>
      </div>
      <FlatCard className="" noBorder={true}>
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <h1 className="text-xl font-medium dark:text-white mr-2">
              {selectedTab === "compute" ? "Usage" : "Inference"} Summary for{" "}
            </h1>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              className="w-60"
              toDate={new Date()}
              requiredRange={true}
            />
          </div>
          <h1 className="text-xl font-medium dark:text-white">
            {orgContext.currencyDisplay === "hours" ? (
              <>Total Usage: {getTotalHours(selectedTab)} GPU hours</>
            ) : (
              <>Total Cost: {getTotalCost(selectedTab)}</>
            )}
          </h1>
        </div>
        <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
        {selectedTab === "compute" && (
          <UsageTable
            displayMode={displayMode}
            usageData={usageData || []}
            usageLoading={usageLoading}
          />
        )}
        {selectedTab === "deployments" && (
          <DeploymentUsageTable
            displayMode={displayMode}
            usageData={usageData || []}
            usageLoading={usageLoading}
          />
        )}
      </FlatCard>
    </>
  );
};

function calc(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

function getCurrentMonth() {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentDate = new Date();
  const currentMonth = monthNames[currentDate.getMonth()];

  return currentMonth;
}

interface InstanceUsageTableProps {
  usages: UsageTableRow[];
}

const InstanceUsageTable: React.FC<InstanceUsageTableProps> = ({ usages }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const orgContext = useContext(OrgContext);

  const currentUsages = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return usages.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, usages]);

  const totalPages = Math.ceil(usages.length / itemsPerPage);
  let table: React.ReactNode;

  if (orgContext.currencyDisplay === "hours") {
    table = (
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Instance Id</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Owner</TableHeaderCell>
              <TableHeaderCell>Total Usage (GPU hours)</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentUsages.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary ">
                    <CopyButton value={item.EnvID} />
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {item.EnvName}
                  </span>
                </TableCell>
                <TableCell>
                  <UsernameDisplayField
                    userId={item.CreatedByUserID}
                    className="text-secondary"
                  />
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {displayGpuHours(item.TotalTime)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableRoot>
    );
  } else {
    table = (
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Instance Id</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Owner</TableHeaderCell>
              <TableHeaderCell>Compute Cost</TableHeaderCell>
              <TableHeaderCell>Storage Cost</TableHeaderCell>
              <TableHeaderCell>Total Cost</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentUsages.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary ">
                    <CopyButton value={item.EnvID} />
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {item.EnvName}
                  </span>
                </TableCell>
                <TableCell>
                  <UsernameDisplayField
                    userId={item.CreatedByUserID}
                    className="text-secondary"
                  />
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {item.ComputeCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {item.StorageCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500 dark:text-secondary">
                    {item.TotalCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableRoot>
    );
  }

  return (
    <>
      {table}
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};

interface UserUsageTableProps {
  usages: UsageTableRow[];
}

const UserUsageTable: React.FC<UserUsageTableProps> = ({ usages }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const orgContext = useContext(OrgContext);

  const currentUsages = useMemo(() => {
    const usage: Record<string, number> = {};
    usages.forEach((item) => {
      if (!usage[item.CreatedByUserID]) {
        usage[item.CreatedByUserID] = 0;
      }
      usage[item.CreatedByUserID] += Number(
        orgContext.currencyDisplay === "hours" ? item.TotalTime : item.TotalCost
      );
    });
    return usage;
  }, [usages]);

  const startIndex = useMemo(() => {
    return (currentPage - 1) * itemsPerPage;
  }, [currentPage]);

  const totalPages = Math.ceil(
    Object.keys(currentUsages).length / itemsPerPage
  );
  let table: React.ReactNode;

  if (orgContext.currencyDisplay === "hours") {
    table = (
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Owner</TableHeaderCell>
              <TableHeaderCell>Total Usage (GPU hours)</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(currentUsages)
              .slice(startIndex, startIndex + itemsPerPage)
              .map(([userId, cost], index) => (
                <TableRow key={index}>
                  <TableCell>
                    <UsernameDisplayField
                      userId={userId}
                      className="text-secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500 dark:text-secondary">
                      {displayGpuHours(cost)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableRoot>
    );
  } else {
    table = (
      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Owner</TableHeaderCell>
              <TableHeaderCell>Total Cost</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(currentUsages)
              .slice(startIndex, startIndex + itemsPerPage)
              .map(([userId, cost], index) => (
                <TableRow key={index}>
                  <TableCell>
                    <UsernameDisplayField
                      userId={userId}
                      className="text-secondary"
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-500 dark:text-secondary">
                      {cost.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableRoot>
    );
  }

  return (
    <>
      {table}
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};

interface UsageTableProps {
  displayMode: DisplayMode;
  usageData: UsageResp[];
  usageLoading: boolean;
}

const UsageTable: React.FC<UsageTableProps> = ({
  displayMode,
  usageData,
  usageLoading,
}) => {
  const transformUsages = (usages: UsageResp[]): UsageTableRow[] => {
    const result: Record<string, UsageTableRow> = {};

    usages
      .filter(
        (usage) =>
          usage.UsageType === "compute" || usage.UsageType === "storage"
      )
      .forEach((usage) => {
        if (!result[usage.EnvID]) {
          result[usage.EnvID] = {
            EnvID: usage.EnvID,
            EnvName: usage.EnvName,
            CreatedByUserID: usage.CreatedByUserID,
            ComputeCost: 0,
            StorageCost: 0,
            TotalCost: 0,
            TotalTime: usage.TotalTime / 3600,
          };
        }
        if (usage.UsageType === "compute") {
          result[usage.EnvID].ComputeCost = parseFloat(usage.Cost);
        } else if (usage.UsageType === "storage") {
          result[usage.EnvID].StorageCost = parseFloat(usage.Cost);
        }
        result[usage.EnvID].TotalCost =
          result[usage.EnvID].ComputeCost + result[usage.EnvID].StorageCost;
      });

    return Object.values(result);
  };

  const getTotalCost = () => {
    let totalCost = 0.0;
    usageData?.forEach((transaction) => {
      if (transaction.Cost) {
        totalCost += parseFloat(transaction.Cost);
      }
    });
    try {
      return `${calc(totalCost).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })}`;
    } catch (error) {
      return totalCost;
    }
  };

  const transformedUsages = useMemo(() => {
    return transformUsages(usageData).sort((a, b) => {
      return b.TotalCost - a.TotalCost;
    });
  }, [usageData]);

  if (usageLoading) {
    return (
      <FlatCard className="w-full justify-center" isLoading={usageLoading}>
        <div className="flex w-full justify-center">
          <span className="text-sm text-gray-700 dark:text-secondary">
            Fetching Usage Data...
          </span>
        </div>
      </FlatCard>
    );
  }

  if (transformedUsages.length === 0) {
    return (
      <FlatCard className="w-full justify-center">
        <div className="flex w-full justify-center">
          <span className="text-sm text-gray-700 dark:text-secondary">
            No Instances With Usage Found
          </span>
        </div>
      </FlatCard>
    );
  }

  return (
    <>
      <div className="flex flex-row w-full mt-3 mb-5">
        {displayMode === "byUser" ? (
          <TopUsers currentUsages={transformedUsages} />
        ) : (
          <TopInstances currentUsages={transformedUsages} />
        )}
      </div>
      <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
      {displayMode === "byUser" ? (
        <UserUsageTable usages={transformedUsages} />
      ) : (
        <InstanceUsageTable usages={transformedUsages} />
      )}
    </>
  );
};

export default UsageSummary;

interface TopInstancesProps {
  currentUsages: UsageTableRow[];
}

const TopInstances: React.FC<TopInstancesProps> = ({ currentUsages }) => {
  const orgContext = useContext(OrgContext);

  const getTopInstances = (
    costType: "TotalCost" | "ComputeCost" | "StorageCost" | "TotalTime"
  ) => {
    return currentUsages
      .sort((a, b) => b[costType] - a[costType])
      .slice(0, 3)
      .map((env) => ({
        name: `${env.EnvName} (${env.EnvID})`,
        value: env[costType],
      }));
  };

  if (orgContext.currencyDisplay === "hours") {
    return (
      <div className="flex flex-row w-1/3">
        <div className="flex flex-col w-full mr-3">
          <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
            <h1 className="text-md font-semibold mr-1">Highest Usage</h1>
          </div>
          <BarList
            className="w-full"
            showAnimation={true}
            data={getTopInstances("TotalTime")}
            valueFormatter={(value) => `${displayGpuHours(value)} GPU hours`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full">
      <div className="flex flex-col w-full mr-3">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">Highest Total Cost</h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={getTopInstances("TotalCost")}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
        />
      </div>

      <div className="flex flex-col w-full mr-3">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">Highest Compute Cost</h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={getTopInstances("ComputeCost")}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
        />
      </div>

      <div className="flex flex-col w-full">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">Highest Storage Cost</h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={getTopInstances("StorageCost")}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
        />
      </div>
    </div>
  );
};

interface TopUsersProps {
  currentUsages: UsageTableRow[];
}

const TopUsers: React.FC<TopUsersProps> = ({ currentUsages }) => {
  const orgContext = useContext(OrgContext);

  const getTopUsers = (
    costType: "TotalCost" | "ComputeCost" | "StorageCost" | "TotalTime"
  ) => {
    const userCost: Record<string, number> = {};
    currentUsages.forEach((usage) => {
      if (!userCost[usage.CreatedByUserID]) {
        userCost[usage.CreatedByUserID] = 0;
      }
      userCost[usage.CreatedByUserID] += Number(usage[costType]);
    });
    return Object.entries(userCost)
      .map(([userId, cost]) => ({ name: userId, value: cost }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);
  };

  if (orgContext.currencyDisplay === "hours") {
    return (
      <div className="flex flex-row w-1/3">
        <div className="flex flex-col w-full mr-3">
          <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
            <h1 className="text-md font-semibold mr-1">Highest Usage</h1>
          </div>
          <BarList
            className="w-full"
            showAnimation={true}
            data={getTopUsers("TotalTime")}
            valueFormatter={(value) => `${displayGpuHours(value)} GPU hours`}
            isUserId={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full">
      <div className="flex flex-col w-full mr-3">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">Highest Total Cost</h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={getTopUsers("TotalCost")}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
          isUserId={true}
        />
      </div>

      <div className="flex flex-col w-full mr-3">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">Highest Compute Cost</h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={getTopUsers("ComputeCost")}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
          isUserId={true}
        />
      </div>

      <div className="flex flex-col w-full">
        <div className="flex flex-row text-gray-900 dark:text-white items-center mb-3">
          <h1 className="text-md font-semibold mr-1">Highest Storage Cost</h1>
        </div>
        <BarList
          className="w-full"
          showAnimation={true}
          data={getTopUsers("StorageCost")}
          valueFormatter={(value) =>
            `${value.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}`
          }
          isUserId={true}
        />
      </div>
    </div>
  );
};
